import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  message, 
  Row, 
  Col, 
  Avatar, 
  Tag, 
  Typography, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Divider, 
  Tooltip, 
  Badge, 
  Popconfirm,
  Empty,
  Spin,
  Tree,
  Collapse
} from 'antd';
import { 
  TeamOutlined, 
  UserOutlined, 
  EditOutlined, 
  SaveOutlined, 
  CloseOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  UserAddOutlined, 
  SwapOutlined, 
  CrownOutlined, 
  SettingOutlined,
  DownOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserRole } from '../utils/cognitoUtils';
import { useApp } from '../contexts/AppContext';
import apiService, { Team, User } from '../services/api';
import { isAdmin } from '../utils/roleUtils';
import DEPLOYMENT_CONFIG from '../config/deployment';
import './Teams.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const Teams: React.FC = () => {
  const { user, isLoading: authLoading } = useCognitoAuth();
  const { refreshCalendarData } = useApp();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [teamForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (user && !authLoading) {
      fetchTeams();
      fetchUsers();
    }
  }, [user, authLoading]);

  const fetchTeams = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await apiService.getTeams();
      setTeams(data);
    } catch (error) {
      message.error('Erro ao carregar equipas.');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    if (!user) return;
    try {
      const data = await apiService.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error('Erro ao carregar utilizadores.');
      setUsers([]);
    }
  };

  const handleCreateTeam = async (values: any) => {
    if (!user) return;
    try {
      await apiService.createTeam(values);
      message.success('Equipa criada com sucesso!');
      setShowTeamModal(false);
      teamForm.resetFields();
      fetchTeams();
      // Refresh calendar data to update dropdown options
      refreshCalendarData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar equipa.';
      message.error(errorMessage);
    }
  };

  const handleUpdateTeam = async (values: any) => {
    if (!editingTeam) return;
    try {
      await apiService.updateTeam(editingTeam.id, values);
      message.success('Equipa atualizada com sucesso!');
      setShowTeamModal(false);
      setEditingTeam(null);
      teamForm.resetFields();
      fetchTeams();
      // Refresh calendar data to update dropdown options
      refreshCalendarData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar equipa.';
      message.error(errorMessage);
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    try {
      await apiService.deleteTeam(teamId);
      message.success('Equipa excluída com sucesso!');
      fetchTeams();
      // Refresh calendar data to update dropdown options
      refreshCalendarData();
    } catch (error) {
      message.error('Erro ao excluir equipa.');
    }
  };

  const handleAddUserToTeam = async (teamId: number, userId: number) => {
    try {
      await apiService.addTeamMember(teamId, userId);
      message.success('Utilizador adicionado à equipa!');
      fetchTeams();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao adicionar utilizador à equipa.';
      message.error(errorMessage);
    }
  };

  const handleRemoveUserFromTeam = async (teamId: number, userId: number) => {
    try {
      await apiService.removeTeamMember(teamId, userId);
      message.success('Utilizador removido da equipa!');
      fetchTeams();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover utilizador da equipa.';
      message.error(errorMessage);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'red';
      case 'diretor_comercial': return 'orange';
      case 'team_leader': return 'blue';
      case 'gestor_equipa': return 'purple';
      case 'gestor_equipa_junior': return 'cyan';
      case 'gestor_comercial': return 'green';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'diretor_comercial': return 'Diretor Comercial';
      case 'team_leader': return 'Team Leader';
      case 'gestor_equipa': return 'Gestor de Equipa';
      case 'gestor_equipa_junior': return 'Gestor de Equipa Júnior';
      case 'gestor_comercial': return 'Gestor Comercial';
      default: return role;
    }
  };

  const getAvatarUrl = (avatarUrl: string | null | undefined) => {
    if (avatarUrl) {
      if (avatarUrl.startsWith('data:image/')) {
        return avatarUrl;
      }
      return `${DEPLOYMENT_CONFIG.getBackendUrl()}/${avatarUrl}`;
    }
    return undefined;
  };

  const renderTeamMembers = (team: Team) => (
    <div style={{ marginTop: 16 }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: 12,
        padding: '8px 0'
      }}>
        <Text strong style={{ fontSize: '14px', color: '#262626' }}>
          Membros ({team.members?.length || 0})
        </Text>
        {team.team_leader_id && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px', marginRight: 8 }}>
              Líder:
            </Text>
            {(() => {
              // First try to find the leader in the users array
              const leader = Array.isArray(users) ? users.find(u => u.id === team.team_leader_id) : null;
              
              // If not found in users array, try to find in team members
              if (!leader && Array.isArray(team.members)) {
                const memberLeader = team.members.find(member => member.id === team.team_leader_id);
                if (memberLeader) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        size="small" 
                        icon={<UserOutlined />}
                        src={getAvatarUrl(memberLeader.avatar_url)}
                        style={{ marginRight: 4 }}
                      />
                      <Text strong style={{ fontSize: '12px' }}>
                        {memberLeader.username}
                      </Text>
                    </div>
                  );
                }
              }
              
              return leader ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />}
                    src={getAvatarUrl(leader.avatar_url)}
                    style={{ marginRight: 4 }}
                  />
                  <Text strong style={{ fontSize: '12px' }}>
                    {leader.username}
                  </Text>
                </div>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Não atribuído
                </Text>
              );
            })()}
          </div>
        )}
      </div>
      
      <div style={{ marginTop: 8 }}>
        {team.members && team.members.length > 0 ? (
          team.members.map(member => (
            <div 
              key={member.id} 
              style={{ 
                marginBottom: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: member.id === team.team_leader_id ? '#e6f7ff' : '#fafafa',
                borderRadius: '8px',
                border: member.id === team.team_leader_id ? '1px solid #91d5ff' : '1px solid #f0f0f0',
                transition: 'all 0.2s ease',
                minHeight: '60px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <Avatar 
                  size={40}
                  icon={<UserOutlined />} 
                  src={getAvatarUrl(member.avatar_url)}
                  style={{ 
                    backgroundColor: getRoleColor(member.role),
                    marginRight: 12,
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: 4,
                    minWidth: 0
                  }}>
                    <Text 
                      strong 
                      style={{ 
                        fontSize: '14px', 
                        color: '#262626',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                      title={member.username}
                    >
                      {member.username}
                    </Text>
                    {member.id === team.team_leader_id && (
                      <Tag 
                        color="blue" 
                        icon={<CrownOutlined />}
                        style={{ marginLeft: 8, fontSize: '11px', flexShrink: 0 }}
                      >
                        Líder
                      </Tag>
                    )}
                  </div>
                  <Tag 
                    color={getRoleColor(member.role)}
                    style={{ fontSize: '12px' }}
                  >
                    {getRoleLabel(member.role)}
                  </Tag>
                </div>
              </div>
              
              {editMode && member.id !== team.team_leader_id && (
                <Popconfirm
                  title="Remover utilizador da equipa?"
                  onConfirm={() => handleRemoveUserFromTeam(team.id, member.id)}
                  okText="Sim"
                  cancelText="Não"
                >
                  <Button 
                    danger 
                    size="small" 
                    icon={<DeleteOutlined />}
                    type="text"
                    style={{ marginLeft: 8, flexShrink: 0 }}
                  />
                </Popconfirm>
              )}
              {editMode && member.id === team.team_leader_id && (
                <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                  Líder da equipa
                </Text>
              )}
            </div>
          ))
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '24px 16px',
            color: '#8c8c8c',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px dashed #d9d9d9'
          }}>
            <UserOutlined style={{ fontSize: '24px', marginBottom: 8 }} />
            <div>Nenhum membro na equipa</div>
          </div>
        )}
      </div>
      
      {editMode && (
        <div style={{ marginTop: 16 }}>
          <Select
            placeholder="Adicionar utilizador à equipa"
            style={{ width: '100%' }}
            onChange={(userId) => handleAddUserToTeam(team.id, userId)}
            allowClear
            size="large"
          >
            {users
              .filter(user => 
                // Don't show users who are already members
                !team.members?.find(member => member.id === user.id) &&
                // Don't show the team leader (they're automatically part of the team)
                user.id !== team.team_leader_id
              )
              .map(user => (
                <Option key={user.id} value={user.id}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      size="small" 
                      icon={<UserOutlined />}
                      src={getAvatarUrl(user.avatar_url)}
                      style={{ marginRight: 8 }}
                    />
                    <span>{user.username}</span>
                    <Tag 
                      color={getRoleColor(user.role)}
                      style={{ marginLeft: 8, fontSize: '11px' }}
                    >
                      {getRoleLabel(user.role)}
                    </Tag>
                  </div>
                </Option>
              ))}
          </Select>
        </div>
      )}
    </div>
  );

  const renderTeamCard = (team: Team) => (
    <Card
      key={team.id}
      title={
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          minHeight: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <TeamOutlined style={{ 
              color: '#1890ff', 
              fontSize: '20px',
              marginRight: 8,
              flexShrink: 0
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text 
                strong 
                style={{ 
                  fontSize: '16px', 
                  color: '#262626',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
                title={team.name}
              >
                {team.name}
              </Text>
              {team.parent_id && (
                <Text 
                  type="secondary" 
                  style={{ 
                    fontSize: '12px',
                    display: 'block',
                    marginTop: '2px'
                  }}
                >
                  Sub-equipa de: {teams.find(t => t.id === team.parent_id)?.name || 'Equipa Pai'}
                </Text>
              )}
            </div>
            {team.team_leader_id && (
              <Tag 
                color="blue" 
                icon={<CrownOutlined />}
                style={{ marginLeft: 8, fontSize: '12px', flexShrink: 0 }}
              >
                Líder
              </Tag>
            )}
            {team.parent_id && (
              <Tag 
                color="orange" 
                style={{ marginLeft: 8, fontSize: '12px', flexShrink: 0 }}
              >
                Sub-equipa
              </Tag>
            )}
          </div>
          
          {editMode && (
            <Space style={{ flexShrink: 0, marginLeft: 16 }}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingTeam(team);
                  teamForm.setFieldsValue({
                    name: team.name,
                    team_leader_id: team.team_leader_id,
                    parent_id: team.parent_id,
                    description: team.description,
                    business_area: team.business_area
                  });
                  setShowTeamModal(true);
                }}
              >
                Editar
              </Button>
              <Popconfirm
                title="Excluir equipa?"
                onConfirm={() => handleDeleteTeam(team.id)}
                okText="Sim"
                cancelText="Não"
              >
                <Button 
                  danger 
                  size="small" 
                  icon={<DeleteOutlined />}
                >
                  Excluir
                </Button>
              </Popconfirm>
            </Space>
          )}
        </div>
      }
      style={{ 
        marginBottom: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        overflow: 'hidden',
        minWidth: '450px'
      }}
      bodyStyle={{ padding: '20px' }}
      headStyle={{ 
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 20px'
      }}
    >
      {team.description && (
        <div style={{ 
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#f6f8fa',
          borderRadius: '8px',
          border: '1px solid #e1e4e8'
        }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {team.description}
          </Text>
        </div>
      )}
      
      {team.business_area && (
        <div style={{ marginBottom: 16 }}>
          <Tag 
            color="geekblue" 
            style={{ 
              fontSize: '12px',
              padding: '4px 8px',
              borderRadius: '6px'
            }}
          >
            {team.business_area === 'telecommunications' ? 'Telecomunicações' : 
             team.business_area === 'energy_solutions' ? 'Soluções Energéticas' : 
             team.business_area}
                  </Tag>
                </div>
      )}

      {renderTeamMembers(team)}

      {team.children && team.children.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider orientation="left" style={{ fontSize: '14px', color: '#262626' }}>
            Sub-equipas ({team.children.length})
          </Divider>
          <Row gutter={[16, 16]}>
            {team.children.map(child => (
              <Col key={child.id} span={24}>
                <Card
                  size="small"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TeamOutlined style={{ fontSize: '14px', marginRight: 6 }} />
                      <Text style={{ fontSize: '14px' }}>{child.name}</Text>
                    </div>
                  }
                  style={{ 
                    backgroundColor: '#fafafa',
                    border: '1px solid #e8e8e8'
                  }}
                  bodyStyle={{ padding: '12px' }}
                >
                  {renderTeamMembers(child)}
                </Card>
              </Col>
                ))}
          </Row>
          </div>
        )}
    </Card>
  );

  if (authLoading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '100px 24px',
        backgroundColor: '#fafafa',
        minHeight: '100vh'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text style={{ fontSize: '16px', color: '#8c8c8c' }}>
            A carregar equipas...
          </Text>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '100px 24px',
        backgroundColor: '#fafafa',
        minHeight: '100vh'
      }}>
        <Text style={{ fontSize: '16px', color: '#8c8c8c' }}>
          Não autenticado. Por favor, faça login.
        </Text>
      </div>
    );
  }

  return (
    <div className="teams-container">
      <div style={{ 
        marginBottom: 32,
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TeamOutlined style={{ 
                fontSize: '28px', 
                color: '#1890ff',
                marginRight: 12
              }} />
              <Title level={2} style={{ margin: 0, color: '#262626' }}>
                Gestão de Equipas
              </Title>
            </div>
          </Col>
          <Col>
            <Space size="middle">
          {isAdmin(getUserRole(user)) && (
            <>
              <Button
                type={editMode ? 'default' : 'primary'}
                icon={editMode ? <CloseOutlined /> : <EditOutlined />}
                onClick={() => setEditMode(!editMode)}
                    size="large"
                    style={{ 
                      borderRadius: '8px',
                      height: '40px',
                      padding: '0 20px'
                    }}
              >
                {editMode ? 'Cancelar Edição' : 'Editar Equipas'}
              </Button>
              {editMode && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingTeam(null);
                    teamForm.resetFields();
                    setShowTeamModal(true);
                  }}
                      size="large"
                      style={{ 
                        borderRadius: '8px',
                        height: '40px',
                        padding: '0 20px'
                      }}
                >
                  Nova Equipa
                </Button>
              )}
            </>
          )}
        </Space>
          </Col>
        </Row>
      </div>

      {/* Search Filter */}
      <div style={{ 
        marginBottom: 24,
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '20px 24px'
      }}>
        <Input
          placeholder="Pesquisar equipas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ 
            borderRadius: '8px',
            maxWidth: '400px'
          }}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          allowClear
        />
      </div>

      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 24px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: '16px', color: '#8c8c8c' }}>
              A carregar equipas...
            </Text>
          </div>
        </div>
      ) : teams.length === 0 ? (
        <div style={{ 
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          padding: '60px 24px'
        }}>
          <Empty
            description="Nenhuma equipa encontrada"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {isAdmin(getUserRole(user)) && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                size="large"
                onClick={() => {
                  setEditingTeam(null);
                  teamForm.resetFields();
                  setShowTeamModal(true);
                }}
                style={{ 
                  borderRadius: '8px',
                  height: '40px',
                  padding: '0 20px'
                }}
              >
                Criar Primeira Equipa
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        <Row gutter={[24, 24]} style={{ margin: 0 }}>
          {teams
            .filter(team => 
              searchTerm === '' || 
              team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()))
            )
            .map(team => (
            <Col key={team.id} xs={24} sm={24} md={24} lg={12} xl={8} style={{ padding: '12px' }}>
              {renderTeamCard(team)}
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            <span>{editingTeam ? 'Editar Equipa' : 'Nova Equipa'}</span>
          </div>
        }
        open={showTeamModal}
        onCancel={() => {
          setShowTeamModal(false);
          setEditingTeam(null);
          teamForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 20 }}
        bodyStyle={{ padding: '24px' }}
      >
        <Form
          form={teamForm}
          layout="vertical"
          onFinish={editingTeam ? handleUpdateTeam : handleCreateTeam}
        >
          <Form.Item
            name="name"
            label="Nome da Equipa"
            rules={[{ required: true, message: 'Por favor insira o nome da equipa!' }]}
          >
            <Input 
              placeholder="Ex: Equipa de Vendas Norte" 
              size="large"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Descrição"
          >
            <Input.TextArea 
              placeholder="Descrição opcional da equipa"
              rows={3}
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="business_area"
            label="Área de Negócio"
          >
            <Select 
              placeholder="Selecionar área de negócio" 
              allowClear
              size="large"
              style={{ borderRadius: '8px' }}
            >
              <Option value="telecommunications">Telecomunicações</Option>
              <Option value="energy_solutions">Soluções Energéticas</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="parent_id"
            label="Equipa Pai"
          >
            <Select 
              placeholder="Selecionar equipa pai (opcional)" 
              allowClear
              size="large"
              style={{ borderRadius: '8px' }}
            >
              {teams
                .filter(t => t.id !== editingTeam?.id)
                .map(team => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="team_leader_id"
            label="Líder da Equipa"
          >
            <Select 
              placeholder="Selecionar líder da equipa" 
              allowClear
              size="large"
              style={{ borderRadius: '8px' }}
            >
              {Array.isArray(users) ? users.map(user => (
                <Option key={user.id} value={user.id}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      size="small" 
                      icon={<UserOutlined />}
                      src={getAvatarUrl(user.avatar_url)}
                      style={{ marginRight: 8 }}
                    />
                    <span>{user.username}</span>
                    <Tag 
                      color={getRoleColor(user.role)}
                      style={{ marginLeft: 8, fontSize: '11px' }}
                    >
                      {getRoleLabel(user.role)}
                    </Tag>
                  </div>
                </Option>
              )) : null}
            </Select>
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button 
                onClick={() => {
                setShowTeamModal(false);
                setEditingTeam(null);
                teamForm.resetFields();
                }}
                size="large"
                style={{ 
                  borderRadius: '8px',
                  height: '40px',
                  padding: '0 20px'
                }}
              >
                Cancelar
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                size="large"
                style={{ 
                  borderRadius: '8px',
                  height: '40px',
                  padding: '0 20px'
                }}
              >
                {editingTeam ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Teams; 